import type { Dsl } from "agent-contracts";
import { loadDslContext } from "./dsl-context.js";

export interface NavigationContextOptions {
  configPath?: string;
  embeddedDsl?: Record<string, unknown>;
  projectDslPath?: string;
  artifactBinding?: {
    source: string;
    mappings?: Record<string, string>;
  };
  paths?: Record<string, string>;
}

export interface NavigationContext {
  dsl: Record<string, unknown>;
  navigationIndex: Record<string, unknown>;
  diagnostics: Array<{ path: string; message: string; severity: string }>;
}

export async function buildNavigationContext(
  options: NavigationContextOptions,
): Promise<NavigationContext> {
  const diagnostics: NavigationContext["diagnostics"] = [];
  let dsl: Record<string, unknown>;

  if (options.embeddedDsl != null) {
    if (options.projectDslPath) {
      const ctx = await loadDslContext({
        embeddedDsl: options.embeddedDsl,
        projectDslPath: options.projectDslPath,
      });
      dsl = ctx.dsl;
    } else {
      dsl = options.embeddedDsl;
    }
  } else if (options.projectDslPath) {
    // No embedded DSL: resolve the project DSL file directly
    const { resolve } = await import("agent-contracts");
    const result = await resolve(options.projectDslPath);
    dsl = result.data;
  } else {
    throw new Error("buildNavigationContext requires embeddedDsl or projectDslPath");
  }

  if (options.artifactBinding) {
    const { resolveBound } = await import("agent-contracts");
    const boundResult = await resolveBound(dsl, {
      artifactBinding: options.artifactBinding,
      paths: options.paths,
    });
    dsl = boundResult.data;
    for (const d of boundResult.diagnostics) {
      diagnostics.push({
        path: d.rule,
        message: d.message,
        severity: d.severity,
      });
    }
  }

  const { validateSchema, buildNavigationIndex } = await import("agent-contracts");
  const validation = validateSchema(dsl);
  for (const d of validation.diagnostics) {
    diagnostics.push({
      path: d.path,
      message: d.message,
      severity: d.severity ?? "error",
    });
  }

  // Use validated Dsl when available; fall back to raw dsl on validation failure
  const navigationIndex = buildNavigationIndex(
    (validation.data ?? dsl) as unknown as Dsl,
  ) as Record<string, unknown>;

  return { dsl, navigationIndex, diagnostics };
}
